import type { Analytics } from 'firebase/analytics';
import { features } from '../../config';

/**
 * Firebase web app configuration (all values are public client config, not
 * secrets — they are provided in the Firebase console under Project settings).
 */
export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId: string;
}

/** Fields that must be present for Firebase Analytics to initialise. */
const REQUIRED_FIELDS: (keyof FirebaseWebConfig)[] = ['apiKey', 'projectId', 'appId', 'measurementId'];

/**
 * Read the Firebase config from Vite env vars (VITE_FIREBASE_*). Returns a
 * partial — completeness is validated separately.
 */
export function readFirebaseConfig(): Partial<FirebaseWebConfig> {
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  };
}

/** True when every field required to initialise analytics has a value. */
export function isAnalyticsConfigComplete(config: Partial<FirebaseWebConfig>): boolean {
  return REQUIRED_FIELDS.every((field) => {
    const value = config[field];
    return typeof value === 'string' && value.trim().length > 0;
  });
}

/** Analytics runs only when the feature flag is on AND the config is complete. */
export function shouldEnableAnalytics(enabled: boolean, config: Partial<FirebaseWebConfig>): boolean {
  return enabled && isAnalyticsConfigComplete(config);
}

let analyticsInstance: Analytics | null = null;

/**
 * Initialise Firebase Analytics once, if enabled and supported in this
 * environment. Firebase modules are dynamically imported so the SDK is only
 * downloaded when analytics is actually turned on. Safe to call fire-and-forget.
 */
export async function initAnalytics(): Promise<Analytics | null> {
  if (analyticsInstance) return analyticsInstance;

  const config = readFirebaseConfig();
  if (!shouldEnableAnalytics(features.analytics, config)) return null;

  try {
    const { initializeApp, getApps } = await import('firebase/app');
    const { getAnalytics, isSupported } = await import('firebase/analytics');

    if (!(await isSupported())) return null;

    const app = getApps().length ? getApps()[0] : initializeApp(config as FirebaseWebConfig);
    analyticsInstance = getAnalytics(app);
    return analyticsInstance;
  } catch (error) {
    console.warn('[analytics] Firebase Analytics failed to initialise', error);
    return null;
  }
}

/**
 * Log a custom analytics event. No-op when analytics is not initialised, so
 * callers never need to guard.
 */
export async function trackEvent(name: string, params?: Record<string, unknown>): Promise<void> {
  if (!analyticsInstance) return;
  const { logEvent } = await import('firebase/analytics');
  logEvent(analyticsInstance, name, params);
}
